import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { AnnouncementAttachments } from '../components/AnnouncementAttachments';
import { BellRing, ChevronDown, ShieldCheck, Megaphone, CheckCircle2, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const dt = (s) => (s ? new Date(s).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }) : '-');
const STATE = {
  pending: ['Onay bekliyor', 'bg-rose-100 text-rose-700'],
  acked: ['Okudum ve anladım', 'bg-emerald-100 text-emerald-700'],
  seen: ['Görüldü', 'bg-slate-100 text-slate-600'],
};

const Item = ({ a, onAcked }) => {
  const [open, setOpen] = useState(a.state === 'pending');
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [label, cls] = STATE[a.state];
  const ack = async () => {
    setBusy(true);
    try { await axios.post(`${API}/firm/announcements/${a.id}/ack`); toast.success('Duyuru onaylandı'); onAcked(); } catch { toast.error('Onaylanamadı, tekrar deneyin'); }
    setBusy(false);
  };
  return (
    <Card className={a.state === 'pending' ? 'border-rose-300' : 'border-slate-200'} data-testid={`firm-ann-${a.id}`}>
      <CardContent className="p-0">
        <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors" data-testid={`firm-ann-toggle-${a.id}`}>
          {a.mode === 'ack' ? <ShieldCheck className="h-5 w-5 text-rose-600 shrink-0" /> : <Megaphone className="h-5 w-5 text-sky-600 shrink-0" />}
          <span className="flex-1 min-w-0">
            <span className="block font-semibold text-slate-900 truncate">{a.title}</span>
            <span className="flex flex-wrap gap-x-4 text-xs text-slate-500">
              {a.seen_at && <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{a.seen_by} · {dt(a.seen_at)}</span>}
              {a.acked_at && <span className="flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3 w-3" />{a.acked_by} · {dt(a.acked_at)}</span>}
            </span>
          </span>
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`} data-testid={`firm-ann-state-${a.id}`}>{label}</span>
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="border-t px-4 py-3 space-y-3">
            <div className="announcement-content text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(a.html || '', { ADD_ATTR: ['target'] }) }} />
            <AnnouncementAttachments aid={a.id} attachments={a.attachments} />
            {a.state === 'pending' && (
              <div className="flex flex-wrap items-center gap-3 rounded-lg bg-rose-50 px-3 py-2">
                {a.block_permits && <p className="w-full text-xs font-medium text-rose-700">Bu duyuruyu onaylamadan yeni talep oluşturamazsınız.</p>}
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer"><Checkbox checked={checked} onCheckedChange={(v) => setChecked(!!v)} data-testid={`firm-ann-check-${a.id}`} />Okudum ve anladım</label>
                <Button size="sm" className="ml-auto bg-rose-600 hover:bg-rose-700" disabled={!checked || busy} onClick={ack} data-testid={`firm-ann-ack-${a.id}`}>Onaylıyorum</Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function FirmAnnouncementsPage() {
  const [items, setItems] = useState(null);
  const load = () => axios.get(`${API}/firm/announcements/archive`).then(r => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const pending = (items || []).filter(a => a.state === 'pending').length;
  return (
    <div className="space-y-6 max-w-4xl" data-testid="firm-announcements-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><BellRing className="h-6 w-6 text-rose-500" />Duyurularım</h1>
        <p className="text-slate-500 mt-1">Firmanıza gönderilen duyurular. Daha önce gördüğünüz ve onayladığınız duyuruları buradan tekrar okuyabilirsiniz.{pending > 0 && <b className="text-rose-600"> {pending} duyuru onayınızı bekliyor.</b>}</p>
      </div>
      {items === null ? <p className="text-slate-400">Yükleniyor...</p>
        : items.length === 0 ? <Card className="border-dashed"><CardContent className="p-10 text-center text-slate-500" data-testid="firm-ann-empty">Henüz duyuru yok.</CardContent></Card>
          : <div className="space-y-3">{items.map(a => <Item key={a.id} a={a} onAcked={() => { load(); window.dispatchEvent(new Event('announcements:refresh')); window.dispatchEvent(new Event('announcements:count')); }} />)}</div>}
    </div>
  );
}
